/** @jsx jsx */
import React, { useCallback, useState } from 'react';
import noop from 'lodash/noop';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner/spinner';
import EmptyState from '@atlaskit/empty-state/empty-state';
import LinkButton from '@atlaskit/button/link';
import Breadcrumbs from '@atlaskit/breadcrumbs/breadcrumbs';
import { BreadcrumbsItem } from '@atlaskit/breadcrumbs/breadcrumbs-item';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import PageHeader from '@jira/platform__common-components-page-header/src/index.tsx';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { ErrorFlag, FlagGroup } from '@atlassian/jira-flags';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	ApplicationLinkOAuth2ConfigInfo,
	ApplicationLinkV3Data,
	ApplicationLinkV3Status,
} from './CommonApplicationLinkCreationEntity.tsx';
import ProductCard from './ProductCard.tsx';
import { lookupTypeByTypeId } from './TypeDefinition.types.tsx';
import { APPLINKS_LIST_PAGE_URL } from './applinksListPageUrl.constants.tsx';
import { ErrorStatusSectionMessage } from './ErrorStatusSectionMessage.tsx';
import { ERRORS } from './Errors.tsx';
import { StatusLozengeV3 } from './StatusLozenge.tsx';
import ApplinksOAuth2EditPageContents from './ApplinksOAuth2EditPageContents.tsx';
import ApplinksOAuth1EditPageContents from './ApplinksOAuth1EditPageContents.tsx';

const applicationLinksTitleMessage = defineMessage({
	defaultMessage: 'Application links',
	id: 'applinks.administration.ui.application-links-title',
	description:
		'Page title displayed at the top of the application links administration page. Shows users they are in the section for managing connections between applications.',
});

const seeAllApplinksButtonMessage = defineMessage({
	defaultMessage: 'See all application links',
	id: 'applinks.administration.ui.edit.see-all-application-links-button',
	description: 'Label for the button which navigates to the page displaying all application links.',
});

const applinkNotFoundHeaderMessage = defineMessage({
	defaultMessage: 'Application link not found',
	id: 'applinks.administration.ui.edit.applink-not-found-header',
	description: 'Header for the section displayed when an application link is not found.',
});

const applinkNotFoundDescriptionMessage = defineMessage({
	defaultMessage: "The application link you're trying to edit does not exist or has been deleted.",
	id: 'applinks.administration.ui.edit.applink-not-found-description',
	description: 'Description for the section displayed when an application link is not found.',
});

const applinkNotEditableHeaderMessage = defineMessage({
	defaultMessage: 'Non-editable system link',
	id: 'applinks.administration.ui.edit.applink-not-editable-header',
	description: 'Header for the section displayed when an application link is not editable.',
});

const editMainTitleMessage = defineMessage({
	defaultMessage: 'Edit application link',
	id: 'applinks.administration.ui.edit.main.title',
	description: 'Title for the main edit application link section.',
});

const securedByMessage = defineMessage({
	defaultMessage: 'Secured by {authMethod}',
	id: 'applinks.administration.ui.edit.secured-by-message',
	description:
		'Message indicating the authentication method used for the application link. {authMethod} will be either "OAuth 1.0" or "OAuth 2.0".',
});

const productCardHeaderMessage = defineMessage({
	defaultMessage: 'Linked application',
	id: 'applinks.administration.ui.edit.product-card-header',
	description: 'Header for the product card displaying linked application details.',
});

const errorFlagTitleMessage = defineMessage({
	defaultMessage: 'Failed to update application link',
	id: 'applinks.administration.ui.edit.error-flag-title',
	description: 'Title for the error flag displayed when an application link update fails.',
});

const systemLinkDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.system-link-description',
	defaultMessage:
		'This is an internal link created automatically by the system. System internal links are read-only and cannot be edited.',
	description:
		'The text is shown as a body paragraph in the status detail panel explaining that the application link is an automatically created internal system link that is read-only and cannot be edited.',
});

const styles = cssMap({
	applinksPageContents: {
		maxWidth: '900px',
		margin: '0 auto',
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
	},
});

const DisplayError = (props: { header: string; description: string }) => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={styles.applinksPageContents}>
			<EmptyState
				header={props.header}
				description={props.description}
				primaryAction={
					<LinkButton href="/plugins/servlet/applinks/listApplicationLinks" appearance="primary">
						{formatMessage(seeAllApplinksButtonMessage)}
					</LinkButton>
				}
			/>
		</Box>
	);
};

export type ApplinksErrorProps = {
	kind: 'ERROR';
	err: 'NOT_EDITABLE';
};

export type ApplinksOAuth1Props = {
	kind: 'OAUTH1';
	applink: ApplicationLinkV3Data;
};

export type ApplinksOAuth2Props = {
	kind: 'OAUTH2';
	applink: ApplicationLinkV3Data;
	oauth2ConfigInfo: ApplicationLinkOAuth2ConfigInfo | null;
};

export type ApplinksProps = ApplinksErrorProps | ApplinksOAuth1Props | ApplinksOAuth2Props;

type ApplinksEditPageContentsProps = {
	isLoading?: boolean;
	data: ApplinksProps | null;
	status: ApplicationLinkV3Status | null;
	onRequestStatusRefresh: () => void;
};

const ApplinksEditPageContents = (props: ApplinksEditPageContentsProps): JSX.Element => {
	const [errorFlags, setErrorFlags] = useState<Array<string>>([]);

	const { formatMessage } = useIntl();

	const breadcrumbs = useCallback(
		() => (
			<Breadcrumbs onExpand={noop}>
				<BreadcrumbsItem
					text={formatMessage(applicationLinksTitleMessage)}
					key="Application Links"
					href={APPLINKS_LIST_PAGE_URL}
				/>
			</Breadcrumbs>
		),
		[formatMessage],
	);

	const addFlag = useCallback(
		(err: string) => {
			const newFlags = errorFlags.slice();
			newFlags.splice(0, 0, err);

			setErrorFlags(newFlags);
		},
		[errorFlags],
	);

	const handleDismiss = () => {
		setErrorFlags(errorFlags.slice(1));
	};

	const renderStatusError = useCallback(() => {
		if (props.status && !props.status.working) {
			if (fg('olympus-1101-fix-disabled-error')) {
				const matchedError =
					props.status.error && props.status.error.type in ERRORS
						? ERRORS[props.status.error.type]
						: ERRORS.UNKNOWN;

				// Don't show a status message for a "disabled" error state
				if (matchedError === ERRORS.DISABLED) {
					return <></>;
				}

				return <ErrorStatusSectionMessage err={matchedError} context={props.status} />;
			}

			return (
				<ErrorStatusSectionMessage
					err={props.status.error ? ERRORS[props.status.error.type] : ERRORS.UNKNOWN}
					context={props.status}
				/>
			);
		}

		return <></>;
	}, [props.status]);

	const renderContents = useCallback(() => {
		if (props.isLoading) {
			return (
				<Inline alignInline="center" space="space.100" xcss={styles.applinksPageContents}>
					<Spinner />
				</Inline>
			);
		}
		if (props.data === null) {
			return (
				<DisplayError
					header={formatMessage(applinkNotFoundHeaderMessage)}
					description={formatMessage(applinkNotFoundDescriptionMessage)}
				/>
			);
		}
		if (props.data.kind === 'ERROR') {
			switch (props.data.err) {
				// TODO: Add more error cases here
				case 'NOT_EDITABLE':
				default:
					return (
						<DisplayError
							header={formatMessage(applinkNotEditableHeaderMessage)}
							description={formatMessage(systemLinkDescriptionMessage)}
						/>
					);
			}
		}
		return (
			<Stack grow="fill" xcss={styles.applinksPageContents}>
				<PageHeader breadcrumbs={breadcrumbs()}>{formatMessage(editMainTitleMessage)}</PageHeader>
				<Stack space="space.200">
					<Inline space="space.050" alignBlock="center">
						<LockLockedIcon label="" />
						<Text>
							{formatMessage(securedByMessage, {
								authMethod: (
									<Text as="strong">
										{props.data.applink.data.isOAuth2 ? 'OAuth 2.0' : 'OAuth 1.0'}
									</Text>
								),
							})}
						</Text>
					</Inline>
					<ProductCard
						applicationType={lookupTypeByTypeId(props.data.applink.type, false)}
						displayUrl={props.data.applink.displayUrl}
						heading={formatMessage(productCardHeaderMessage)}
						statusLozenge={<StatusLozengeV3 status={props.status} />}
					/>
					{renderStatusError()}
				</Stack>
				{props.data.kind === 'OAUTH2' ? (
					<ApplinksOAuth2EditPageContents
						applink={props.data.applink}
						oauth2ConfigInfo={props.data.oauth2ConfigInfo}
						onDisplayError={addFlag}
						onRequestStatusRefresh={props.onRequestStatusRefresh}
					/>
				) : (
					<ApplinksOAuth1EditPageContents
						applink={props.data.applink}
						status={props.status}
						onDisplayError={addFlag}
					/>
				)}
			</Stack>
		);
	}, [
		addFlag,
		breadcrumbs,
		formatMessage,
		props.data,
		props.isLoading,
		props.onRequestStatusRefresh,
		props.status,
		renderStatusError,
	]);

	return (
		<>
			<JSErrorBoundary
				id="applinks-administration-edit-page"
				teamName="olympus"
				packageName="applinksAdministrationEditPage"
			>
				<ModalContextProvider>{renderContents()}</ModalContextProvider>
				<FlagGroup onDismissed={handleDismiss}>
					{errorFlags.map((err, idx) => {
						return (
							<ErrorFlag
								id={`flag-${idx}`}
								testId={`applinks-administration.ui.edit.error-flag-${idx}`}
								key={`flag-${idx}`}
								title={formatMessage(errorFlagTitleMessage)}
								description={err}
							/>
						);
					})}
				</FlagGroup>
			</JSErrorBoundary>
		</>
	);
};

export default ApplinksEditPageContents;
